import { useRef, useState, type ReactNode } from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '../lib/utils';

interface HoverScrollTextProps {
  children: ReactNode;
  /** Plain-text form, used for the tooltip fallback */
  title?: string;
  className?: string;
  /** Reveal speed in pixels per second */
  speed?: number;
}

/**
 * Text that is clipped when it does not fit its container, and slides sideways
 * to reveal the rest while hovered or keyboard-focused.
 *
 * Measured on hover rather than on render, so it costs nothing until used and
 * stays correct when the container is resized. Entries that already fit never
 * move - only genuine overflow animates.
 */
export function HoverScrollText({ children, title, className, speed = 70 }: HoverScrollTextProps) {
  const viewport = useRef<HTMLSpanElement>(null);
  const content = useRef<HTMLSpanElement>(null);
  const [shift, setShift] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  const reveal = () => {
    if (prefersReducedMotion) return;

    const outer = viewport.current;
    const inner = content.current;
    if (!outer || !inner) return;

    // Sub-pixel widths round badly, so ignore anything under a pixel
    const overflow = inner.scrollWidth - outer.clientWidth;
    setShift(overflow > 1 ? overflow : 0);
  };

  const settle = () => setShift(0);

  return (
    <span
      ref={viewport}
      className={cn('inline-block overflow-hidden align-bottom', className)}
      onMouseEnter={reveal}
      onMouseLeave={settle}
      onFocus={reveal}
      onBlur={settle}
      title={title}
    >
      <span
        ref={content}
        className="inline-block whitespace-nowrap"
        style={{
          transform: `translateX(${-shift}px)`,
          // Constant speed regardless of distance, so a long name does not
          // crawl while a short one flicks past. Snaps back quickly on leave.
          transition: `transform ${shift > 0 ? shift / speed : 0.2}s linear`,
          willChange: shift > 0 ? 'transform' : undefined,
        }}
      >
        {children}
      </span>
    </span>
  );
}
